import { useMemo, useState } from "react";
import { LoanCalculatorWrapper } from "./LoanCalculatorStyles";

const formatCurrency = (value) => {
  if (!Number.isFinite(value)) {
    return "0.00";
  }
  return value.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [duration, setDuration] = useState("");
  const [durationUnit, setDurationUnit] = useState("years");
  const hasAnyInput = loanAmount || interestRate || duration;

  const parsedValues = useMemo(() => {
    const principal = Number(loanAmount);
    const annualRate = Number(interestRate);
    const durationValue = Number(duration);

    const years = durationUnit === "months" ? durationValue / 12 : durationValue;
    const months = durationUnit === "years" ? durationValue * 12 : durationValue;

    const isValid =
      Number.isFinite(principal) &&
      Number.isFinite(annualRate) &&
      Number.isFinite(years) &&
      principal > 0 &&
      annualRate >= 0 &&
      years > 0 &&
      months > 0;

    if (!isValid) {
      return {
        isValid: false,
        simpleInterest: 0,
        simpleTotalAmount: 0,
        emiMonthlyRepayment: 0,
        emiTotalRepayment: 0,
        emiTotalInterest: 0,
      };
    }

    const simpleInterest = (principal * annualRate * years) / 100;
    const simpleTotalAmount = principal + simpleInterest;

    const monthlyRate = annualRate / 12 / 100;
    const installmentCount = Math.round(months);
    let emiMonthlyRepayment = 0;

    if (monthlyRate === 0) {
      emiMonthlyRepayment = principal / installmentCount;
    } else {
      const ratePower = Math.pow(1 + monthlyRate, installmentCount);
      emiMonthlyRepayment =
        (principal * monthlyRate * ratePower) / (ratePower - 1);
    }

    const emiTotalRepayment = emiMonthlyRepayment * installmentCount;
    const emiTotalInterest = emiTotalRepayment - principal;

    return {
      isValid: true,
      simpleInterest,
      simpleTotalAmount,
      emiMonthlyRepayment,
      emiTotalRepayment,
      emiTotalInterest,
    };
  }, [loanAmount, interestRate, duration, durationUnit]);

  let resultContent = null;

  if (hasAnyInput) {
    if (parsedValues.isValid) {
      resultContent = (
        <div className="result">
          <h3>Simple Interest Result</h3>
          <p>Total Interest: N{formatCurrency(parsedValues.simpleInterest)}</p>
          <p>Total Amount To Return: N{formatCurrency(parsedValues.simpleTotalAmount)}</p>

          <h3>EMI Mode Result</h3>
          <p>Monthly Repayment (EMI): N{formatCurrency(parsedValues.emiMonthlyRepayment)}</p>
          <p>Total Interest: N{formatCurrency(parsedValues.emiTotalInterest)}</p>
          <p>Total Amount To Return: N{formatCurrency(parsedValues.emiTotalRepayment)}</p>
        </div>
      );
    } else {
      resultContent = (
        <p className="error">
          Please enter valid values: amount and duration must be greater than
          zero, and interest cannot be negative.
        </p>
      );
    }
  }

  return (
    <LoanCalculatorWrapper>
      <div className="card">
        <h1>Loan Calculator</h1>
        <p className="subtitle">
          Enter loan amount, annual interest rate, and duration to calculate
          both simple-interest and EMI monthly repayment results.
        </p>

        <form>
          <div>
            <label htmlFor="loanAmount">Loan Amount</label>
            <input
              id="loanAmount"
              name="loanAmount"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 500000"
              value={loanAmount}
              onChange={(event) => setLoanAmount(event.target.value)}
            />
          </div>

          <div>
            <label htmlFor="interestRate">Annual Interest Rate (%)</label>
            <input
              id="interestRate"
              name="interestRate"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 12"
              value={interestRate}
              onChange={(event) => setInterestRate(event.target.value)}
            />
          </div>

          <div>
            <label htmlFor="duration">Duration</label>
            <input
              id="duration"
              name="duration"
              type="number"
              min="0"
              step="0.1"
              placeholder={durationUnit === "years" ? "e.g. 2" : "e.g. 24"}
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
            />
          </div>

          <div>
            <label htmlFor="durationUnit">Duration Unit</label>
            <select
              id="durationUnit"
              name="durationUnit"
              value={durationUnit}
              onChange={(event) => setDurationUnit(event.target.value)}
            >
              <option value="years">Years</option>
              <option value="months">Months</option>
            </select>
          </div>
        </form>

        {resultContent}
      </div>
    </LoanCalculatorWrapper>
  );
};

export default LoanCalculator;
